import React, { useEffect, useMemo, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SubMatFolderIcon from '../G04docuSubMatFolder/SubMatFolderIcon';
import { buildChildrenMap, shouldShowTestComparisonCheckbox, isTestComparisonMasterRoot, collectDescendantComparisonIds, itemHasTestComparison } from './subMatTemplateTocOrder';

const collectDescendantIds = (itemId, childrenMap) => {
  const ids = [itemId];
  (childrenMap[itemId] || []).forEach((k) => {
    ids.push(...collectDescendantIds(k.id, childrenMap));
  });
  return ids;
};

const displayTitle = (item) => {
  if (item.titleOverride?.trim()) return item.titleOverride.trim();
  return item.folderName || item.folderId;
};

/** dropPosition: before | after | inside */
const applyDrop = (items, dragId, targetId, dropPosition) => {
  if (!dragId || !targetId || dragId === targetId) return items;
  const childrenMap = buildChildrenMap(items);
  const descendants = new Set(collectDescendantIds(dragId, childrenMap));
  if (descendants.has(targetId)) return items;

  const target = items.find((it) => it.id === targetId);
  const drag = items.find((it) => it.id === dragId);
  if (!target || !drag) return items;

  const newParentId = dropPosition === 'inside'
    ? target.id
    : (target.parentItemId || null);

  const parentKey = newParentId || '';
  const siblings = (childrenMap[parentKey] || [])
    .filter((s) => s.id !== dragId)
    .map((s) => s.id);

  let insertAt;
  if (dropPosition === 'inside') {
    insertAt = siblings.length;
  } else {
    const targetIdx = siblings.indexOf(targetId);
    insertAt = dropPosition === 'before' ? targetIdx : targetIdx + 1;
    if (insertAt < 0) insertAt = siblings.length;
  }
  siblings.splice(insertAt, 0, dragId);

  const orderById = {};
  siblings.forEach((id, i) => { orderById[id] = i + 1; });

  return items.map((it) => {
    if (it.id === dragId) {
      return {
        ...it,
        parentItemId: newParentId,
        sortOrder: orderById[it.id],
      };
    }
    if (orderById[it.id] != null) {
      return { ...it, sortOrder: orderById[it.id] };
    }
    return it;
  });
};

const resolveDropPosition = (e, el) => {
  const rect = el.getBoundingClientRect();
  const ratio = (e.clientY - rect.top) / Math.max(rect.height, 1);
  if (ratio < 0.28) return 'before';
  if (ratio > 0.72) return 'after';
  return 'inside';
};

const TocRow = ({
  item,
  hasChildren,
  expanded,
  onToggleExpand,
  onUpdate,
  onToggleIncluded,
  onToggleComparison,
  childrenMap,
  items,
  onContextMenu,
  dragId,
  dropHint,
  dragIdRef,
  onDragStart,
  onDragEnd,
  onDragOverRow,
  onDropOnRow,
  onPreview,
  previewLoadingId,
}) => {
  const folderStub = { currentFile: item.hasCurrentFile ? {} : null };
  const isDragging = dragId === item.id;
  const hintHere = dropHint?.targetId === item.id ? dropHint.position : null;
  const subtreeIds = collectDescendantIds(item.id, childrenMap);
  const includedCount = subtreeIds.filter((id) => (
    items.find((it) => it.id === id)?.included !== false
  )).length;
  const includedChecked = includedCount === subtreeIds.length;
  const includedIndeterminate = includedCount > 0 && includedCount < subtreeIds.length;
  const showComparisonCheckbox = shouldShowTestComparisonCheckbox(item, childrenMap);
  const descendantCmpIds = isTestComparisonMasterRoot(item)
    ? collectDescendantComparisonIds(item.id, childrenMap)
    : [];
  const comparisonTargets = itemHasTestComparison(item)
    ? [item.id, ...descendantCmpIds.filter((id) => id !== item.id)]
    : descendantCmpIds;
  const checkedCount = comparisonTargets.filter((id) => (
    items.find((it) => it.id === id)?.includeTestComparison === true
  )).length;
  const comparisonChecked = comparisonTargets.length > 0
    ? checkedCount === comparisonTargets.length
    : item.includeTestComparison === true;
  const comparisonIndeterminate = comparisonTargets.length > 0
    && checkedCount > 0
    && checkedCount < comparisonTargets.length;

  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item.id);
        dragIdRef.current = item.id;
        onDragStart(item.id);
      }}
      onDragEnd={() => {
        dragIdRef.current = null;
        onDragEnd();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOverRow(item.id, resolveDropPosition(e, e.currentTarget));
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = e.dataTransfer.getData('text/plain') || dragIdRef.current;
        onDropOnRow(id, item.id, resolveDropPosition(e, e.currentTarget));
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(e, item);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: '8px',
        py: 0.25,
        minHeight: 32,
        opacity: isDragging ? 0.35 : (item.included === false ? 0.55 : 1),
        cursor: 'grab',
        userSelect: 'none',
        bgcolor: hintHere === 'inside' ? 'action.selected' : 'transparent',
        boxShadow: hintHere === 'before'
          ? (theme) => `inset 0 2px 0 ${theme.palette.primary.main}`
          : hintHere === 'after'
            ? (theme) => `inset 0 -2px 0 ${theme.palette.primary.main}`
            : 'none',
        '&:hover': { bgcolor: hintHere ? undefined : 'action.hover' },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {/* MUI TreeItem iconContainer: width 15, marginRight 4 */}
      <Box
        sx={{
          width: 15,
          mr: '4px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': { fontSize: 18 },
        }}
      >
        {hasChildren ? (
          <Box
            component="span"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(item.id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggleExpand(item.id);
              }
            }}
            sx={{
              display: 'inline-flex',
              cursor: 'pointer',
              lineHeight: 0,
              color: 'inherit',
            }}
          >
            {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </Box>
        ) : null}
      </Box>
      {/* MUI TreeItem label: paddingLeft 4 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flex: 1,
          minWidth: 0,
          pl: '4px',
        }}
      >
        <Checkbox
          size="small"
          checked={includedChecked}
          indeterminate={includedIndeterminate}
          onChange={(e) => onToggleIncluded(item.id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{ p: 0.25 }}
        />
        <SubMatFolderIcon folder={folderStub} />
        <Typography
          variant="body2"
          component="span"
          noWrap
          sx={{ flex: 1, minWidth: 0 }}
          color={item.folderMissing ? 'error.main' : 'text.primary'}
        >
          {displayTitle(item)}
          {item.folderMissing && ' (폴더 없음)'}
        </Typography>
        {onPreview && item.hasCurrentFile === true && item.folderId && !item.folderMissing && (
          <Tooltip
            title="문서 보기"
            placement="top"
            enterDelay={400}
            disableInteractive
            PopperProps={{ sx: { pointerEvents: 'none' } }}
          >
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
              <IconButton
                size="small"
                aria-label="문서 보기"
                disabled={previewLoadingId === item.id}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview?.(item);
                }}
                sx={{ p: 0.25 }}
              >
                {previewLoadingId === item.id
                  ? <CircularProgress size={16} />
                  : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {(showComparisonCheckbox) && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Checkbox
              size="small"
              checked={comparisonChecked}
              indeterminate={comparisonIndeterminate}
              onChange={(e) => onToggleComparison(item.id, e.target.checked)}
              sx={{ p: 0.25 }}
            />
            <Typography variant="caption" color="text.secondary" noWrap>
              시험성과대비표 합본 포함
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** MUI TreeItem-group: marginLeft 17 only */
const TocBranch = ({
  nodes,
  childrenMap,
  expandedSet,
  onToggleExpand,
  ...rowProps
}) => (
  <>
    {nodes.map((item) => {
      const kids = childrenMap[item.id] || [];
      const hasChildren = kids.length > 0;
      const open = expandedSet.has(item.id);
      return (
        <Box key={item.id}>
          <TocRow
            item={item}
            hasChildren={hasChildren}
            expanded={open}
            onToggleExpand={onToggleExpand}
            childrenMap={childrenMap}
            {...rowProps}
          />
          {hasChildren && open && (
            <Box sx={{ ml: '17px' }}>
              <TocBranch
                nodes={kids}
                childrenMap={childrenMap}
                expandedSet={expandedSet}
                onToggleExpand={onToggleExpand}
                {...rowProps}
              />
            </Box>
          )}
        </Box>
      );
    })}
  </>
);

/** sendMode: 발송 직전 — 제외·순서 + 표시명 변경(팝업). 제거는 숨김
 *  bare: 바깥 테두리 없음 (SendSection 안에 넣을 때)
 */
const SubMatTemplateTocTree = ({
  items,
  onChange,
  sendMode = false,
  bare = false,
  onPreview,
  previewLoadingId = '',
}) => {
  const [expanded, setExpanded] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [dropHint, setDropHint] = useState(null);
  const [menu, setMenu] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const dragIdRef = useRef(null);

  const childrenMap = useMemo(() => buildChildrenMap(items), [items]);
  const roots = childrenMap[''] || [];
  const expandedSet = useMemo(() => new Set(expanded), [expanded]);

  useEffect(() => {
    setExpanded(items.map((it) => it.id));
  }, [items.length]);

  const onUpdate = (id, patch) => {
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const onToggleIncluded = (id, checked) => {
    const ids = new Set(collectDescendantIds(id, childrenMap));
    onChange(items.map((it) => (
      ids.has(it.id) ? { ...it, included: checked } : it
    )));
  };

  const onToggleComparison = (id, checked) => {
    const item = items.find((it) => it.id === id);
    if (!item) return;
    const ids = new Set();
    if (isTestComparisonMasterRoot(item)) {
      collectDescendantComparisonIds(id, childrenMap).forEach((cid) => ids.add(cid));
    }
    if (itemHasTestComparison(item) || isTestComparisonMasterRoot(item)) {
      ids.add(id);
    }
    if (!ids.size) return;
    onChange(items.map((it) => (
      ids.has(it.id) ? { ...it, includeTestComparison: checked } : it
    )));
  };

  const onRemove = (id) => {
    const removeIds = new Set(collectDescendantIds(id, childrenMap));
    onChange(items.filter((it) => !removeIds.has(it.id)));
  };

  const handleDrop = (sourceId, targetId, position) => {
    if (!sourceId) return;
    onChange(applyDrop(items, sourceId, targetId, position));
    dragIdRef.current = null;
    setDragId(null);
    setDropHint(null);
  };

  const openRename = (it) => {
    if (!it) return;
    setRenameItem(it);
    setRenameValue(it.titleOverride || it.folderName || '');
  };

  const rowProps = {
    onUpdate,
    onToggleIncluded,
    onToggleComparison,
    childrenMap,
    items,
    onContextMenu: (e, it) => {
      setMenu({
        mouseX: e.clientX,
        mouseY: e.clientY,
        item: it,
      });
    },
    dragId,
    dropHint,
    dragIdRef,
    onDragStart: setDragId,
    onDragEnd: () => { setDragId(null); setDropHint(null); },
    onDragOverRow: (targetId, position) => setDropHint({ targetId, position }),
    onDropOnRow: handleDrop,
    onPreview,
    previewLoadingId,
  };

  if (!items.length) {
    return (
      <Box
        sx={{
          height: 200,
          overflow: 'hidden',
          ...(bare
            ? { p: 0.5 }
            : { border: 1, borderColor: 'divider', borderRadius: 1, p: 0.5 }),
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          {sendMode
            ? '발송할 목차 항목이 없습니다.'
            : '서류를 선택한 뒤 「추가」하세요.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          minHeight: 200,
          p: 0.5,
          ...(bare
            ? {}
            : { border: 1, borderColor: 'divider', borderRadius: 1 }),
        }}
      >
        <TocBranch
          nodes={roots}
          childrenMap={childrenMap}
          expandedSet={expandedSet}
          onToggleExpand={(id) => {
            setExpanded((prev) => (
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
            ));
          }}
          {...rowProps}
        />
      </Box>

      <Menu
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.mouseY, left: menu.mouseX } : undefined}
      >
        <MenuItem
          onClick={() => {
            const it = menu?.item;
            setMenu(null);
            openRename(it);
          }}
        >
          이름 변경
        </MenuItem>
        {!sendMode && (
          <>
            <Divider />
            <MenuItem
              sx={{ color: 'error.main' }}
              onClick={() => {
                const id = menu?.item?.id;
                setMenu(null);
                if (id) onRemove(id);
              }}
            >
              제거
            </MenuItem>
          </>
        )}
      </Menu>

      <Dialog
        open={Boolean(renameItem)}
        onClose={() => setRenameItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>이름 변경</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            폴더명: {renameItem?.folderName || renameItem?.folderId}
            {' '}· 비우면 폴더명 사용 · 목차·합본 표시용
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="표시명"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameItem) {
                onUpdate(renameItem.id, { titleOverride: renameValue.trim() || null });
                setRenameItem(null);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              if (!renameItem) return;
              onUpdate(renameItem.id, { titleOverride: renameValue.trim() || null });
              setRenameItem(null);
            }}
          >
            저장
          </Button>
          <Button onClick={() => setRenameItem(null)}>취소</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubMatTemplateTocTree;
